import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/shared/hooks/use-toast";
import { useImageUpload } from "./useImageUpload";
import { useLutUpload } from "./useLutUpload";
import { useGalleryOperations } from "./useGalleryOperations";
import { usePresetSubmission } from "./usePresetSubmission";
import { useCustomMutation } from "@/shared/hooks/useCustomMutation";
import CategoryService from "@/features/categories/service/categoryService";

export const usePresetForm = (presetData, isEditing, packIdFromPage) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  // Campos básicos
  const [name, setName] = useState(presetData?.name || "");
  const [description, setDescription] = useState(
    presetData?.translations?.[0]?.about || ""
  );
  const [isFree, setIsFree] = useState<boolean>(presetData?.isFree || false);
  const [tags, setTags] = useState(
    Array.isArray(presetData?.tags)
      ? presetData.tags.join(", ")
      : presetData?.tags || ""
  );
  const [published, setPublished] = useState(presetData?.published || false);
  // Extrair categoryIds - verifica tanto 'categories' quanto 'CategoriesPresets'
  const initialCategoryIds = presetData?.categories?.map(cat => cat.id) || 
                            presetData?.CategoriesPresets?.map(cp => cp.categoryId) || [];
  
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>(initialCategoryIds);
  const [originalCategoryIds, setOriginalCategoryIds] = useState<string[]>(initialCategoryIds);

  // Atualizar todos os campos quando presetData mudar (quando dados chegarem da API)
  useEffect(() => {
    if (presetData) {
      setName(presetData.name || "");
      setDescription(presetData.translations?.[0]?.about || "");
      setIsFree(presetData.isFree || false);
      setTags(
        Array.isArray(presetData.tags)
          ? presetData.tags.join(", ")
          : presetData.tags || ""
      );
      setPublished(presetData.published || false);
      
      const newCategoryIds = presetData.categories?.map(cat => cat.id) || 
                            presetData.CategoriesPresets?.map(cp => cp.categoryId) || [];
      setSelectedCategoryIds(newCategoryIds);
      setOriginalCategoryIds(newCategoryIds);
    }
  }, [presetData]);

  // Upload de arquivos
  const imageUpload = useImageUpload({
    initialImage: presetData?.cover || null
  });
  const lutUpload = useLutUpload({
    initialLutFilename: presetData?.lut || null
  });
  // Galeria
  const galleryOps = useGalleryOperations(presetData?.id, isEditing);

  // Submissão
  const { handleSubmit, createPresetMutation, updatePresetMutation, deletePresetMutation } = usePresetSubmission();

  // Mutations para categoria
  const associateCategoryMutation = useCustomMutation({
    serviceInstance: CategoryService.Instance,
    selectAction: (service) => service.associatePresetToCategory,
  });

  const removeCategoryMutation = useCustomMutation({
    serviceInstance: CategoryService.Instance,
    selectAction: (service) => service.removePresetFromCategory,
  });

  // Handler de submit unificado
  const onSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const packId = packIdFromPage || presetData?.packId;
    

    const result = await handleSubmit(
      isFree,
      isEditing,
      packId,
      presetData?.id,
      name,
      description,
      tags,
      imageUpload.imageFile,
      lutUpload.lutFile,
      published,
      galleryOps.uploadGalleryImages
    );
    
    if (isEditing) {
      await galleryOps.commitGalleryDeletions();
    }

    
    if (result && result !== 0) {
      const presetId = typeof result === 'string' ? result : presetData?.id;
      if (presetId) {
        try {
          await handleCategoryAssociation(presetId);
          navigate(-1);
        } catch (error) {
          console.error('Erro ao processar categorias:', error);
          toast({
            title: "Aviso",
            description: "Preset salvo, mas houve erro ao processar algumas categorias.",
            variant: "destructive",
          });
          navigate(-1);
        }
      } else {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  // Função para gerenciar associação de categorias
  const handleCategoryAssociation = async (presetId: string) => {

    
    const previousCategoryIds = originalCategoryIds;
    const newCategoryIds = selectedCategoryIds;

    // Categorias para remover (estavam selecionadas antes, mas não estão mais)
    const categoriesToRemove = previousCategoryIds.filter(id => !newCategoryIds.includes(id));
    
    // Categorias para adicionar (não estavam selecionadas antes, mas estão agora)
    const categoriesToAdd = newCategoryIds.filter(id => !previousCategoryIds.includes(id));
    


    // Executar todas as operações de remoção em paralelo
    const removePromises = categoriesToRemove.map(async (categoryId) => {
      try {
        await removeCategoryMutation.mutateAsync({
          categoryId: categoryId,
          presetId: presetId
        });
      } catch (error) {
        console.error('Erro ao remover preset da categoria:', categoryId, error);
        throw error;
      }
    });

    // Executar todas as operações de adição em paralelo
    const addPromises = categoriesToAdd.map(async (categoryId) => {
      try {
        await associateCategoryMutation.mutateAsync({
          categoryId: categoryId,
          presetId: presetId
        });
      } catch (error) {
        console.error('Erro ao associar preset à categoria:', categoryId, error);
        throw error;
      }
    });

    // Aguardar todas as operações serem concluídas
    try {
      await Promise.all([...removePromises, ...addPromises]);
    } catch (error) {
      console.error('Erro ao processar operações de categoria:', error);
      throw error;
    }
  };
  const handleIsFreeChange = (e: boolean) => {
    setIsFree(e);
  };

  return {
    name,
    setName,
    description,
    setDescription,
    tags,
    setTags,
    published,
    setPublished,
    // Category state and handlers
    selectedCategoryIds,
    setSelectedCategoryIds,
    // Image upload state and handlers
    imagePreview: imageUpload.imagePreview,
    imageFile: imageUpload.imageFile,
    imageError: imageUpload.imageError,
    handleImageChange: imageUpload.handleImageChange,
    setImagePreview: imageUpload.setImagePreview,
    setImageFile: imageUpload.setImageFile,

    // LUT upload state and handlers
    lutFilename: lutUpload.lutFilename,
    lutFile: lutUpload.lutFile,
    lutPreview: lutUpload.lutUrl, // Adicionando o preview do LUT
    lutError: lutUpload.lutError,
    handleLutChange: lutUpload.handleLutChange,
    setLutFilename: lutUpload.setLutFilename,
    setLutFile: lutUpload.setLutFile,
    setLutUrl: lutUpload.setLutUrl, // Expondo o setLutUrl
    ...galleryOps,
    onSubmit,
    isFree,
    handleIsFreeChange,
    createPresetMutation,
    updatePresetMutation,
    deletePresetMutation,
    // Category mutations
    associateCategoryMutation,
    removeCategoryMutation,
    
  };
};
